import type { JSX } from 'react'
import type { CardContentTypeT, CardSizeT, CardTypeT } from 'blocks/Cards'
import type { IRowContainerProps } from '../RowContainer.h'

import { CardSize, CardType } from 'blocks/Cards'
import { SimpleRowSkeletonItems } from 'blocks/Skeletons'
import { useClientResolution } from 'utils/hooks'
import { GRID, LAYOUT, ROW_GAP } from 'vars/index'
import { getRowCardAspectRatio, getRowCardAspectRatioVar } from '../lib'
import {
  RowContainerVerticalGaps,
  RowControlsType,
  SpecialRowContainerBackground,
} from '../RowContainer.h'
import { RowSimpleContainer } from '../RowSimpleContainer'
import { useSimpleVisibleCards } from '../RowSimpleContainer/hooks/useSimpleVisibleCards'

export interface IRowSimpleTemplateProps extends Omit<
  IRowContainerProps,
  'visibleCards'
> {
  size: CardSizeT
  type: CardTypeT
  contentType: CardContentTypeT
  /**
   * FP-4261: the episodes carousel of the series details page (Figma
   * `16273:212314`). Opt-in so every other row keeps the shared paddings,
   * gap and visible-card table.
   */
  detailsEpisodesRow?: boolean
}

export const RowSimpleTemplate = ({
  verticalPaddingBottom: _unused1,
  verticalPaddingTop: _unused2,
  containerVerticalGap = RowContainerVerticalGaps.RowDefault,
  itemCount,
  children,
  gap = ROW_GAP.SIZE_10,
  controlsType = RowControlsType.inRowFluid,
  title,
  scrollByCardsCount,
  titleColor,
  tvChannelCard,
  buttonTitle = 'See all',
  loaded: _unused3 = true,
  selectedItem,
  size = CardSize.L,
  type = CardType.HORIZONTAL,
  contentType,
  backgroundStyle,
  isSpecialRow,
  isRowLoading,
  detailsEpisodesRow = false,
  horizontalPaddings,
  ...rest
}: IRowSimpleTemplateProps): JSX.Element => {
  const { width: clientWidth } = useClientResolution()

  const visibleCards = useSimpleVisibleCards({
    clientWidth,
    contentType,
    size,
    type,
  })

  const rowCardAspectRatio = getRowCardAspectRatio(type)
  const rowCardAspectRatioVar = getRowCardAspectRatioVar(type)

  const verticalPaddingBottomRow = detailsEpisodesRow
    ? GRID.PaddingBottomDetailsEpisodes
    : isSpecialRow
      ? LAYOUT.VerticalTopSpecialSimpleRowPaddings
      : LAYOUT.VerticalBottomRowPaddings
  const verticalPaddingTopRow = detailsEpisodesRow
    ? GRID.PaddingTopDetailsEpisodes
    : isSpecialRow
      ? LAYOUT.VerticalBottomSpecialSimpleRowPaddings
      : LAYOUT.VerticalTopRowPaddings
  // The episodes row has no title, but `RowSimpleContainer` still treats a
  // missing `descriptionType` as a header and would keep `RowDefault` gap
  // (20px from 1240). That stacked on `PaddingTopDetailsEpisodes` and made
  // the season-description-to-cards gap 44px at desktop (FP-4324).
  const containerVerticalGapRow = detailsEpisodesRow
    ? RowContainerVerticalGaps.RowWithoutHeader
    : containerVerticalGap

  return (
    <RowSimpleContainer
      gap={detailsEpisodesRow ? GRID.GapDetailsEpisodes : gap}
      visibleCards={visibleCards}
      verticalPaddingBottom={verticalPaddingBottomRow}
      verticalPaddingTop={verticalPaddingTopRow}
      withSpecialCell={false}
      controlsType={controlsType}
      title={title}
      scrollByCardsCount={scrollByCardsCount}
      titleColor={titleColor}
      tvChannelCard={tvChannelCard}
      itemCount={itemCount}
      buttonTitle={buttonTitle}
      cardAspectRatio={rowCardAspectRatio}
      cardAspectRatioVar={rowCardAspectRatioVar}
      selectedItem={selectedItem}
      backgroundStyle={
        // FP-4322: this details-page band owns the fill; ignore any passed backgroundStyle
        detailsEpisodesRow
          ? SpecialRowContainerBackground.ConstantDark
          : backgroundStyle
      }
      horizontalPaddings={
        detailsEpisodesRow
          ? GRID.HorizontalPaddingsDetailsEpisodes
          : horizontalPaddings
      }
      {...rest}
      containerVerticalGap={containerVerticalGapRow}
      matchPosterHeight={detailsEpisodesRow}
    >
      {isRowLoading ? (
        <SimpleRowSkeletonItems visibleCards={visibleCards} />
      ) : (
        children
      )}
    </RowSimpleContainer>
  )
}
